import { graphql } from 'gatsby';
import { Box, Heading, VideoPlayer } from 'lucid-components-react';
import React from 'react';

import LucidContainer from 'components/lucid-container/lucid-container';
import NineteenCta, {
  nineteenCtaThemes
} from 'components/nineteen/nineteen-cta/nineteen-cta';
import {
  Drupal_FieldParagraphNineteenArticleFieldNineteenCtas,
  Drupal_ParagraphNineteenArticle
} from 'generated/graphql';
import { getProcessedValue } from 'utils/helpers';

interface Props {
  entity: Drupal_ParagraphNineteenArticle;
}

const NineteenArticle = ({
  entity: {
    fieldHeading,
    fieldSubheader,
    fieldImage,
    fieldBody,
    fieldMirror,
    fieldAltText,
    fieldVideoLink,
    fieldAutoplay,
    fieldNineteenCtas
  }
}: Props) => {
  return (
    <Box className="nineteen-article" py={5}>
      <LucidContainer
        flex
        css={theme => ({
          flexWrap: 'wrap',
          [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
            flexWrap: 'nowrap'
          }
        })}
      >
        <Box
          className="nineteen-article__image"
          css={theme => ({
            order: 0,
            paddingBottom: theme.space[5],
            [`@media screen and (min-width: ${theme.breakpoints.tablet})`]: {
              order: 1,
              paddingBottom: 0
            }
          })}
          flex={{
            default: '0 1 100%',
            tablet: '0 1 55%'
          }}
          order={1}
        >
          {fieldVideoLink ? (
            <VideoPlayer
              controls
              css={theme => ({
                boxShadow: theme.shadows.video
              })}
              placeholder={() =>
                fieldImage && <img alt={fieldAltText || ''} src={fieldImage} />}
              playing={fieldAutoplay || false}
              url={fieldVideoLink}
            />
          ) : (
            <img alt={fieldAltText || ''} src={fieldImage!} />
            )}
        </Box>
        <Box
          className="nineteen-article__body"
          flex={{ default: '0 1 100%', tablet: '0 1 40%' }}
          order={fieldMirror ? 0 : 2}
        >
          <Heading
            as="h2"
            mb={{ default: 4, mobile: 5, tablet: 4 }}
            variant="subheadS"
          >
            {fieldHeading}
          </Heading>
          {fieldSubheader && (
            <Heading as="h4" mb={{ default: 1 }} variant="subheadXXS">
              {fieldSubheader}
            </Heading>
          )}
          <Box
            className="nineteen-article__user-content"
            css={theme => ({
              ul: {
                paddingLeft: theme.space[3],
                margin: '0',
                li: {
                  marginBottom: theme.space[4]
                }
              }
            })}
            dangerouslySetInnerHTML={{ __html: getProcessedValue(fieldBody) }}
          />
          {fieldNineteenCtas && (
            <Box>
              {(fieldNineteenCtas as Drupal_FieldParagraphNineteenArticleFieldNineteenCtas[]).map(
                ({ entity }, index) => (
                  <NineteenCta
                    key={entity!.entityId}
                    entity={entity!}
                    primary={index === 0}
                    theme={nineteenCtaThemes.retro}
                  />
                )
              )}
            </Box>
          )}
        </Box>
      </LucidContainer>
    </Box>
  );
};

export default NineteenArticle;

export const query = graphql`
  fragment nineteenArticle on Drupal_ParagraphNineteenArticle {
    entityId
    fieldBody {
      processed
    }
    fieldHeading
    fieldSubheader
    fieldVideoLink
    fieldAutoplay
    fieldMirror
    fieldImage
    fieldAltText
    fieldBeaconElementDetail
    fieldNineteenCtas {
      entity {
        ...nineteenCta
      }
    }
  }
`;
